import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Drawer, Flex, Table, Tag } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { PrintableRootPage } from 'components/page/page-2';
import { useGetOrganizations } from 'lib/hooks/tanstack/useGetOrganizations';
import { useGetCurrentClinicianInfo } from 'lib/hooks/tanstack/use-getCurrentClinicianInfo';
import { useAppSelector } from 'lib/store/rootReducer';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { useState, useMemo } from 'react';
import { AddOrganizationDrawer } from './drawer-add-organisation';
import { useErrorList } from 'lib/utils/useErrorList';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { useOrganisationMutations } from './hooks/useOrganisationMutations';
import { ColumnType } from 'antd/lib/table';
import { Clinic } from 'lib/documents/clinic';
import { formatISO } from 'date-fns';

export function OrganisationManagementPage() {
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const queryClient = useQueryClient();

  function showDrawer() {
    setIsDrawerVisible(true);
  }

  function handleCloseDrawer() {
    setIsDrawerVisible(false);
  }

  function handleSuccess() {
    setIsDrawerVisible(false);
    queryClient.invalidateQueries({ queryKey: ['organizations'] });
  }

  const organisationsQuery = useGetOrganizations();
  const errorList = useErrorList();
  useQueryErrorNotifier(organisationsQuery, errorList, 'Organisations');

  const currentUser = useAppSelector(state => state.auth.user);
  const clinicianInfoQuery = useGetCurrentClinicianInfo(currentUser?.uid);
  useQueryErrorNotifier(clinicianInfoQuery, errorList, 'ClinicianInfo');

  const { joinMutation, leaveMutation, operationStatus } =
    useOrganisationMutations(currentUser?.uid, errorList);

  const columns = useMemo<ColumnType<Clinic>[]>(
    () => [
      {
        title: 'Business Name',
        dataIndex: 'name',
        key: 'name'
      },
      {
        title: 'Contact Email',
        dataIndex: 'contactEmail',
        key: 'contactEmail'
      },
      {
        title: 'Phone Number',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber'
      },
      {
        title: 'City',
        dataIndex: 'city',
        key: 'city'
      },
      {
        title: 'Country',
        dataIndex: 'country',
        key: 'country'
      },
      {
        title: 'Product Program',
        dataIndex: 'productProgram',
        key: 'productProgram',
        render: a => <Tag>{a}</Tag>
      },
      {
        title: 'Joined',
        render: (_, a) =>
          a.metadata
            ? formatISO(a.metadata?.created.toDate(), {
                representation: 'date'
              })
            : null
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_: any, record: any) => {
          const isMember = clinicianInfoQuery.data?.clinics?.includes(
            record.id
          );
          const isPending = joinMutation.isPending || leaveMutation.isPending;

          const isCurrentJoinStatus =
            operationStatus?.clinicId === record.id &&
            operationStatus?.type === 'join';
          const isCurrentLeaveStatus =
            operationStatus?.clinicId === record.id &&
            operationStatus?.type === 'leave';

          let joinIcon = undefined;
          if (isCurrentJoinStatus) {
            if (operationStatus?.status === 'success') {
              joinIcon = <CheckOutlined style={{ color: '#52c41a' }} />;
            } else if (operationStatus?.status === 'error') {
              joinIcon = <CloseOutlined style={{ color: '#ff4d4f' }} />;
            }
          }

          let leaveIcon = undefined;
          if (isCurrentLeaveStatus) {
            if (operationStatus?.status === 'success') {
              leaveIcon = <CheckOutlined style={{ color: '#52c41a' }} />;
            } else if (operationStatus?.status === 'error') {
              leaveIcon = <CloseOutlined style={{ color: '#ff4d4f' }} />;
            }
          }

          return (
            <Flex gap={8} align="center">
              <BaseButton
                type="primary"
                disabled={isMember || isPending}
                loading={
                  joinMutation.isPending && joinMutation.variables === record.id
                }
                onClick={() => joinMutation.mutate(record.id)}
                icon={joinIcon}
              >
                Join
              </BaseButton>

              <BaseButton
                danger
                disabled={!isMember || isPending}
                loading={
                  leaveMutation.isPending &&
                  leaveMutation.variables === record.id
                }
                onClick={() => leaveMutation.mutate(record.id)}
                icon={leaveIcon}
              >
                Leave
              </BaseButton>
            </Flex>
          );
        }
      }
    ],
    [clinicianInfoQuery.data, joinMutation, leaveMutation, operationStatus]
  );

  return (
    <PrintableRootPage title="Organization Management">
      <ErrorListDisplay errorList={errorList} closeable />
      <Flex gap={16} vertical>
        <Flex justify="space-between">
          <div />
          <div>
            <BaseButton type="primary" onClick={showDrawer}>
              Add an organiaztion
            </BaseButton>
          </div>
        </Flex>

        <Table
          dataSource={organisationsQuery.data}
          columns={columns}
          rowKey="id"
          loading={organisationsQuery.isLoading}
        />
      </Flex>

      <Drawer
        title="Add New Organization"
        width={720}
        onClose={handleCloseDrawer}
        open={isDrawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <AddOrganizationDrawer
          onCancel={handleCloseDrawer}
          onSuccess={handleSuccess}
        />
      </Drawer>
    </PrintableRootPage>
  );
}
